import { formatApplicantId, formatPhoneNumber } from '@/features/application/utils/applicationFormatters'

/** Angular `report-paymentlogs-detail`: invoice name is `first_name` + `last_name`. */
export function paymentInvoiceForLabel(log: Record<string, unknown> | null | undefined): string {
  if (!log) return 'Unknown'
  const first = String(log.first_name ?? '').trim()
  const last = String(log.last_name ?? '').trim()
  const fullName = `${first} ${last}`.trim()
  if (fullName) return fullName
  const paidBy = String(log.paid_by ?? '').trim()
  if (paidBy) return paidBy
  return 'Unknown'
}

/** Angular "Application ID" row uses padded `applicant_id` (8 digits). */
export function resolvePaymentLogApplicantId(
  log: Record<string, unknown> | null | undefined,
): number | null {
  if (!log) return null
  const raw = log.applicant_id ?? log.fk_applicant_id
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function formatPaymentLogApplicantIdDisplay(
  log: Record<string, unknown> | null | undefined,
): string {
  const id = resolvePaymentLogApplicantId(log)
  return id != null ? formatApplicantId(id) : ''
}

export function buildPaidByAddressLines(log: Record<string, unknown> | null | undefined): string[] {
  if (!log) return []
  const lines: string[] = []
  const name = paymentInvoiceForLabel(log)
  if (name && name !== 'Unknown') lines.push(name)

  const email = String(log.email ?? '').trim()
  if (email) lines.push(email)

  const phone = log.mobile_no != null && String(log.mobile_no).trim() !== ''
    ? formatPhoneNumber(String(log.mobile_no))
    : ''
  lines.push(phone || 'No Phone')

  const streetParts = [
    log.house_no,
    log.street,
    log.street_type,
  ]
    .map((p) => (p != null ? String(p).trim() : ''))
    .filter(Boolean)
  if (streetParts.length) lines.push(streetParts.join(' '))

  const city = String(log.city ?? '').trim()
  const state = String(log.state ?? '').trim()
  const zip = String(log.zip_code ?? '').trim()
  const cityLine = [city, state, zip].filter(Boolean).join(', ')
  if (cityLine) lines.push(cityLine)

  return lines
}

export function paymentLogTransactionLabel(log: Record<string, unknown> | null | undefined): string {
  if (!log) return '-'
  const source = String(log.payment_source ?? '').toUpperCase()
  if (source === 'ACH' || source === 'PAPER_CHECK') {
    const auth = log.auth_code != null ? String(log.auth_code).trim() : ''
    if (auth) return auth
  }
  const txn = log.transaction_id != null ? String(log.transaction_id).trim() : ''
  if (txn) return txn
  const auth = log.auth_code != null ? String(log.auth_code).trim() : ''
  return auth || '-'
}

export function paymentLogPaidAddressTitle(
  paymentType: unknown,
  isRefund: boolean,
): string {
  if (String(paymentType ?? '') === 'DEFAULT') return 'Paid By'
  return isRefund ? 'Paid To' : 'Paid By'
}

export function paymentLogPaymentAddressTitle(
  paymentType: unknown,
  isRefund: boolean,
): string {
  if (String(paymentType ?? '') === 'DEFAULT') return 'Payment To'
  return isRefund ? 'Payment By' : 'Payment To'
}
